import React from "react";
import { inject, observer } from "mobx-react";
import { withStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Button from "@material-ui/core/Button";
import { Grid, MenuItem, Typography } from "@material-ui/core";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import Autocomplete from "@material-ui/lab/Autocomplete";
import ForgeDestinationCheckboxes from "./ForgeDestinationCheckboxes";

const styles = theme => ({
  videoTitle: {
    marginBottom: "0.75rem"
  },
  durationBox: {
    position: "absolute",
    right: "24px",
    color: "white",
    background: "rgba(0,0,0,.7)",
    top: "20px",
    padding: "4px 6px",
    borderRadius: "5px"
  },
  allowOptionsToShow: {
    overflow: "visible",
    maxWidth: "800px",
    minWidth: "700px"
  }
});

const ITEM_REMOVED = "remove-option";
const ALL_ITEMS_REMOVED = "clear";
const ITEM_SELECTED = "select-option";

class SendToForgeDialog extends React.Component {
  render() {
    const {
      sendToForgeDialogStore,
      gameMetadataStore,
      sportsReferenceStore,
      typeaheadDropDownStore,
      classes
    } = this.props;
    const { forgeVideoPost } = sendToForgeDialogStore;

    const clearAutoCompleteSearchData = () => {
      typeaheadDropDownStore.clearSuggestions();
      typeaheadDropDownStore.clearPreviousSelections();
    };

    const handleOnInputChange = (value, reason) => {
      if (reason === "input") {
        if (value) {
          typeaheadDropDownStore.fetchTags(value);
        }
      } else if (reason === ALL_ITEMS_REMOVED) {
        sendToForgeDialogStore.selectedTags = [];
        clearAutoCompleteSearchData();
      }
    };

    // the user selected or removed something.
    const handleTypeAheadOnChange = (event, values, detail) => {
      if (detail === ITEM_REMOVED) {
        sendToForgeDialogStore.selectedTags = values; // These are the remaining chips
        typeaheadDropDownStore.resetPreviousSelections(values);
      } else if (detail === ITEM_SELECTED) {
        // add another selection, if we haven't already added it.
        values.forEach(v => {
          sendToForgeDialogStore.addTag(v);
        });
      }
      typeaheadDropDownStore.clearSuggestions();
    };

    return (
      <div>
        <Dialog
          open={sendToForgeDialogStore.isOpen}
          onClose={() => {
            sendToForgeDialogStore.reset();
            typeaheadDropDownStore.clearSuggestions();
          }}
          aria-labelledby="form-dialog-title"
          classes={{ paperScrollPaper: classes.allowOptionsToShow }}
        >
          <DialogTitle>
            Send To Forge
            <Typography className={classes.durationBox}>
              {forgeVideoPost.formattedDuration}
            </Typography>
          </DialogTitle>
          <DialogContent className={classes.allowOptionsToShow}>
            <img
              className={classes.thumbnail}
              src={forgeVideoPost.thumbnailUrl}
              alt="clip-thumbnail"
              height={"200"}
            />

            <TextField
              required
              autoFocus
              fullWidth
              className={classes.videoTitle}
              margin="dense"
              label="Title"
              value={forgeVideoPost.videoTitle}
              onChange={e => {
                forgeVideoPost.videoTitle = e.target.value;
              }}
            />
            <TextField
              margin="dense"
              label="Description"
              fullWidth
              className={classes.videoDescription}
              value={forgeVideoPost.videoDescription}
              onChange={e => {
                forgeVideoPost.videoDescription = e.target.value;
              }}
            />

            <Autocomplete
              multiple
              style={{ marginTop: "1rem" }}
              value={sendToForgeDialogStore.selectedTags}
              options={typeaheadDropDownStore.suggestions.slice()} // convert to JS array instead of MobX
              getOptionSelected={(option, value) => {
                return option.value === value.value;
              }}
              getOptionLabel={option => {
                return option && option.label ? option.label : "";
              }}
              id={`auto-complete-forge-tags`}
              onInputChange={(event, value, reason) =>
                handleOnInputChange(value, reason)
              }
              onChange={(event, value, detail) =>
                handleTypeAheadOnChange(event, value, detail)
              }
              renderInput={params => (
                <TextField
                  {...params}
                  onKeyDown={e => e.stopPropagation()}
                  label={"Tags"}
                  helperText={"Begin typing to see suggestions"}
                  onBlur={() => {
                    typeaheadDropDownStore.clearSuggestions();
                  }}
                  InputProps={{
                    ...params.InputProps,
                    type: "search"
                  }}
                />
              )}
            />

            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "1rem",
                marginTop: "1rem",
                paddingBottom: "1.5rem"
              }}
            >
              <Grid container style={{ flex: 1 }}>
                <Grid item xs={3} style={{ paddingRight: "8px" }}>
                  <InputLabel id="sport-id-label" shrink={true}>
                    Sport
                  </InputLabel>
                  <Select
                    id="sport-selector-select"
                    label="Sport"
                    labelId={"sport-id-label"}
                    value={gameMetadataStore.sportId}
                    onChange={e => gameMetadataStore.setSportId(e.target.value)}
                    fullWidth
                  >
                    {sportsReferenceStore.sports.map(s => (
                      <MenuItem key={s.code} value={s.id}>
                        {s.abbreviation}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>

                <Grid item xs={3} style={{ paddingRight: "8px" }}>
                  <TextField
                    id="date"
                    label="Date"
                    type="date"
                    value={gameMetadataStore.date.toISOString().slice(0, 10)}
                    onChange={e => {
                      gameMetadataStore.setDate(new Date(e.target.value));
                    }}
                    fullWidth
                    InputLabelProps={{
                      shrink: true
                    }}
                  />
                </Grid>

                <Grid item xs={6}>
                  <InputLabel id="game-label" shrink={true}>
                    Game
                  </InputLabel>
                  <Select
                    id="game-selector-select"
                    label="Game"
                    labelId={"game-label"}
                    defaultValue={""}
                    disabled={gameMetadataStore.games.length === 0}
                    onChange={e => {
                      const tag = `gamepk-${e.target.value}`;
                      const tagOption = { label: tag, value: tag };
                      sendToForgeDialogStore.addTag(tagOption);
                    }}
                    fullWidth
                  >
                    {gameMetadataStore.games.map(g => (
                      <MenuItem key={g.gamePk} value={g.gamePk}>
                        {g.description}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
              </Grid>
              <ForgeDestinationCheckboxes store={sendToForgeDialogStore} />
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                sendToForgeDialogStore.reset();
                typeaheadDropDownStore.clearSuggestions();
              }}
              color="primary"
            >
              Cancel
            </Button>
            <Button
              disabled={
                forgeVideoPost.videoTitle === "" ||
                !sendToForgeDialogStore.hasValidDestinationSelection
              }
              color="primary"
              onClick={() => {
                sendToForgeDialogStore.sendVideo();
                sendToForgeDialogStore.reset();
                typeaheadDropDownStore.clearSuggestions();
              }}
            >
              Send
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}

export default inject(
  "sendToForgeDialogStore",
  "gameMetadataStore",
  "sportsReferenceStore",
  "typeaheadDropDownStore"
)(withStyles(styles)(observer(SendToForgeDialog)));



// WEBPACK FOOTER //
// ./src/components/SendToForgeDialog.js